import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";

const BASE = "/system/sequences";

const toUiSequence = (row = {}) => ({
  id: row.id,
  name: row.name || "",
  type: row.sequence_type || "",
  prefix: row.prefix || "",
  numericLength: Number(row.numeric_length ?? 0),
  currentValue: Number(row.current_value ?? 0),
  resetType: row.reset_type || "NEVER_RESET",
  lastResetAt: row.last_reset_at || null,
  status: Boolean(row.status),
  lockedAfterUse: Boolean(row.is_locked_after_use),
  appliesToDocType: row.document_type || "",
  systemWide: Boolean(row.applies_system_wide),
  effectiveFrom: row.effective_from_date || "",
  updatedAt: row.updated_at || "",
  updatedBy: row?.last_modified?.changed_by ?? row.updated_by ?? null,
});

const toApiPayload = (payload = {}) => ({
  name: payload.name?.trim(),
  sequence_type: payload.type,
  document_type: payload.appliesToDocType?.trim(),
  prefix: payload.prefix?.trim(),
  numeric_length: Number(payload.numericLength),
  current_value: Number(payload.currentValue),
  reset_type: payload.resetType,
  status: Boolean(payload.status),
  is_locked_after_use: Boolean(payload.lockedAfterUse),
  applies_system_wide: Boolean(payload.systemWide),
  effective_from_date: payload.effectiveFrom || null,
});

export const getSequences = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

export const createSequence = (payload) =>
  axiosInstance.post(BASE, toApiPayload(payload)).then((res) => res.data);

export const updateSequence = (id, payload) =>
  axiosInstance.put(`${BASE}/${id}`, toApiPayload(payload)).then((res) => res.data);

export const getSequenceAuditLogs = (id, params = {}) =>
  axiosInstance.get(`${BASE}/${id}/audit`, { params }).then((res) => res.data);

export const useGetSequences = (params = {}, enabled = true) => {
  const {
    page = 1,
    limit = 10,
    search = "",
    type = "",
    active = "",
    sortBy = "id",
    sortOrder = "ASC",
  } = params;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(BASE, {
        params: { page, limit, search, type, active, sortBy, sortOrder },
      });
      const list = response?.data?.data || [];
      setData(Array.isArray(list) ? list.map(toUiSequence) : []);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load sequences";
      setError(msg);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, type, active, sortBy, sortOrder]);

  useEffect(() => {
    if (!enabled) return;
    fetchData();
  }, [enabled, fetchData]);

  return { data, loading, error, refetch: fetchData };
};

export const useCreateSequence = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      return await createSequence(payload);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create sequence";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useUpdateSequence = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      return await updateSequence(id, payload);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to update sequence";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

export const useGetSequenceAuditLogs = (sequenceId, params = {}, enabled = true) => {
  const {
    page = 1,
    limit = 100,
    sortBy = "changed_at",
    sortOrder = "DESC",
  } = params;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!sequenceId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`${BASE}/${sequenceId}/audit`, {
        params: { page, limit, sortBy, sortOrder },
      });
      const logs = response?.data?.data?.audit_logs || [];
      setData(Array.isArray(logs) ? logs : []);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load audit logs";
      setError(msg);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [sequenceId, page, limit, sortBy, sortOrder]);

  useEffect(() => {
    if (!enabled || !sequenceId) return;
    fetchData();
  }, [enabled, sequenceId, fetchData]);

  return { data, loading, error, refetch: fetchData };
};
